/**
 * Toolbar WhatsApp, variáveis e atalho de IA do editor de follow-up.
 */
import React from 'react';
import { Bold, Italic, Strikethrough, List, ListOrdered, Eraser, Sparkles } from 'lucide-react';
import { FOLLOW_UP_VARIABLE_CHIPS } from '../followUp.constants';
import type { WhatsAppFormat } from '../followUpWhatsAppFormat';

const TOOLBAR: { command: WhatsAppFormat; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { command: 'bold', label: 'Negrito (*texto*)', Icon: Bold },
    { command: 'italic', label: 'Itálico (_texto_)', Icon: Italic },
    { command: 'strike', label: 'Riscado (~texto~)', Icon: Strikethrough },
    { command: 'bulletList', label: 'Lista', Icon: List },
    { command: 'orderedList', label: 'Lista numerada', Icon: ListOrdered },
];

const HINT_DEFAULT = 'Clique em ✨ para reescrever a mensagem com IA. Variáveis são preenchidas no envio.';
const HINT_REWRITING = 'Reescrevendo com IA…';

interface FollowUpMessageToolbarProps {
    rewriting: boolean;
    onFormat: (command: WhatsAppFormat) => void;
    onRewrite: () => void;
    onInsertVariable: (tag: string) => void;
}

export default function FollowUpMessageToolbar({
    rewriting,
    onFormat,
    onRewrite,
    onInsertVariable,
}: FollowUpMessageToolbarProps) {
    return (
        <>
            <div className="flex flex-wrap items-center gap-s1 border-b border-border bg-surface-subtle px-s2 py-s1">
                {TOOLBAR.map(({ command, label, Icon }) => (
                    <button
                        key={command}
                        type="button"
                        title={label}
                        aria-label={label}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onFormat(command)}
                        className="inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md text-secondary hover:bg-surface hover:text-default md:min-h-8 md:min-w-8"
                    >
                        <Icon className="size-4" />
                    </button>
                ))}
                <button
                    type="button"
                    title="Limpar formatação"
                    aria-label="Limpar formatação"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onFormat('clear')}
                    className="inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md text-secondary hover:bg-surface hover:text-default md:min-h-8 md:min-w-8"
                >
                    <Eraser className="size-4" />
                </button>
                <button
                    type="button"
                    title="Reescrever com IA"
                    aria-label="Reescrever mensagem com IA"
                    disabled={rewriting}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onRewrite}
                    className={
                        rewriting
                            ? 'ml-auto inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md bg-primary-subtle text-primary opacity-80 md:min-h-8 md:min-w-8'
                            : 'ml-auto inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md text-secondary hover:bg-surface hover:text-primary md:min-h-8 md:min-w-8'
                    }
                >
                    <Sparkles className={`size-4 ${rewriting ? 'animate-pulse' : ''}`} aria-hidden="true" />
                </button>
            </div>

            <div className="flex flex-col gap-s2 border-b border-border bg-surface-subtle px-s3 py-s2">
                <div className="flex flex-wrap items-center gap-s1">
                    <span className="mr-s1 font-normal text-xs text-muted">Variáveis</span>
                    {FOLLOW_UP_VARIABLE_CHIPS.map(({ label, tag }) => (
                        <button
                            key={tag}
                            type="button"
                            title={`Inserir ${tag}`}
                            aria-label={`Inserir variável ${label}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onInsertVariable(tag)}
                            className="min-h-11 touch-manipulation rounded-md border border-border bg-surface px-s2 py-s1 font-normal text-xs text-default hover:bg-surface-subtle md:min-h-0"
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <p
                    className={`m-0 font-normal text-xs leading-snug ${rewriting ? 'text-primary' : 'text-muted'}`}
                    aria-live="polite"
                >
                    {rewriting ? HINT_REWRITING : HINT_DEFAULT}
                </p>
            </div>
        </>
    );
}
